<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PipelineAPI from '../../../../../../api/pipelineCards';
import Modal from 'dashboard/components/Modal.vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  notes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['itemUpdated', 'goToTab']);

const { t } = useI18n();
const { showAlert } = useAlert();
const store = useStore();

// Upload modal state
const showUploadModal = ref(false);
const uploadProgress = ref(0);
const isUploading = ref(false);
const uploadError = ref('');
const uploadSuccess = ref(false);
const isDragging = ref(false);
const selectedFile = ref(null);

const getAllAttachments = computed(() => {
  const noteAttachmentsList = props.notes.reduce((attachments, note) => {
    if (note.attachments && Array.isArray(note.attachments)) {
      return [
        ...attachments,
        ...note.attachments.map(attachment => ({
          ...attachment,
          source: {
            type: 'note',
            id: note.id,
            date: note.created_at,
            text:
              (note.text?.substring(0, 50) || '') +
              ((note.text?.length || 0) > 50 ? '...' : ''),
          },
        })),
      ];
    }
    return attachments;
  }, []);

  const itemDetailsAttachments =
    props.item.item_details?.attachments?.map(attachment => ({
      ...attachment,
      source: {
        type: 'item',
        id: props.item.id,
      },
    })) || [];

  const activeStorageAttachments =
    props.item.attachments?.map(attachment => ({
      id: attachment.id,
      filename: attachment.filename,
      fileName: attachment.filename,
      content_type: attachment.content_type,
      byte_size: attachment.byte_size,
      fileSize: attachment.byte_size,
      url: attachment.url,
      source: {
        type: 'item',
        id: props.item.id,
      },
    })) || [];

  const allAttachments = [
    ...noteAttachmentsList,
    ...itemDetailsAttachments,
    ...activeStorageAttachments,
  ];
  const seen = new Set();
  return allAttachments.filter(att => {
    const key = att.id?.toString();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

const formatFileSize = bytes => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / k ** i).toFixed(2)) + ' ' + sizes[i];
};

const openUploadModal = () => {
  showUploadModal.value = true;
  uploadProgress.value = 0;
  uploadError.value = '';
  uploadSuccess.value = false;
  selectedFile.value = null;
};

const closeUploadModal = () => {
  showUploadModal.value = false;
  uploadProgress.value = 0;
  uploadError.value = '';
  uploadSuccess.value = false;
  selectedFile.value = null;
  isDragging.value = false;
};

const handleDragEnter = e => {
  e.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = e => {
  e.preventDefault();
  isDragging.value = false;
};

const handleDragOver = e => {
  e.preventDefault();
};

const handleDrop = e => {
  e.preventDefault();
  isDragging.value = false;
  const files = e.dataTransfer.files;
  if (files.length > 0) {
    selectedFile.value = files[0];
  }
};

const handleFileSelect = event => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
  }
};

const uploadSelectedFile = async () => {
  if (!selectedFile.value) return;

  isUploading.value = true;
  uploadError.value = '';
  uploadProgress.value = 0;

  let progressInterval = null;

  try {
    const formData = new FormData();
    formData.append('attachment', selectedFile.value);

    progressInterval = setInterval(() => {
      if (uploadProgress.value < 90) {
        uploadProgress.value += 10;
      }
    }, 100);

    const response = await fetch(
      `/api/v1/accounts/${store.getters.getCurrentAccountId}/pipeline/cards/${props.item.id}/attachments`,
      {
        method: 'POST',
        body: formData,
        headers: {
          api_access_token: store.getters.getCurrentUser?.access_token,
        },
      }
    );

    if (!response.ok) {
      clearInterval(progressInterval);
      progressInterval = null;
      throw new Error(t('PIPELINE.MESSAGES.UPLOAD_ERROR'));
    }

    clearInterval(progressInterval);
    progressInterval = null;
    uploadProgress.value = 100;
    uploadSuccess.value = true;

    emit('itemUpdated');
    emit('goToTab', 'attachments');

    setTimeout(() => {
      closeUploadModal();
    }, 1500);
  } catch (error) {
    if (progressInterval !== null) {
      clearInterval(progressInterval);
    }
    uploadError.value = error.message || t('PIPELINE.MESSAGES.UPLOAD_ERROR');
    uploadProgress.value = 0;
  } finally {
    isUploading.value = false;
  }
};

const handleAttachmentDownload = async attachment => {
  try {
    const { data } = await PipelineAPI.downloadAttachment(
      props.item.id,
      attachment.id
    );

    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.filename || attachment.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    emit('itemUpdated');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[AttachmentsTab] handleAttachmentDownload failed:', error);
    showAlert(t('PIPELINE.MESSAGES.ATTACHMENT_DOWNLOAD_ERROR'), 'error');
  }
};

const handleAttachmentDelete = async attachment => {
  // eslint-disable-next-line no-restricted-globals, no-alert
  if (!confirm(t('PIPELINE.MESSAGES.CONFIRM_DELETE_ATTACHMENT'))) return;

  try {
    await PipelineAPI.deleteAttachment(props.item.id, attachment.id);
    emit('itemUpdated');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[AttachmentsTab] handleAttachmentDelete failed:', error);
    showAlert(t('PIPELINE.MESSAGES.ATTACHMENT_DELETE_ERROR'), 'error');
  }
};
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ t('PIPELINE.ATTACHMENTS_LABEL') }}
      </h3>
      <button
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-brand bg-n-alpha-1 hover:bg-n-alpha-2 rounded-lg transition-colors"
        @click="openUploadModal"
      >
        <fluent-icon icon="add" size="14" />
        {{ t('PIPELINE.ADD_ATTACHMENT') }}
      </button>
    </div>
    <div class="grid grid-cols-1 gap-3">
      <div
        v-for="attachment in getAllAttachments"
        :key="attachment.id"
        class="p-3 bg-n-solid-3 rounded-lg"
      >
        <div class="flex items-center gap-3">
          <fluent-icon
            icon="document"
            size="20"
            class="text-n-slate-10 flex-shrink-0"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ attachment.filename || attachment.fileName }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ formatFileSize(attachment.byte_size || attachment.fileSize) }}
            </p>
          </div>
          <div class="flex items-center gap-1">
            <button
              class="p-1.5 text-n-slate-10 hover:text-n-brand hover:bg-n-alpha-1 rounded transition-colors"
              :title="t('PIPELINE.DOWNLOAD_ATTACHMENT')"
              @click="handleAttachmentDownload(attachment)"
            >
              <fluent-icon icon="arrow-download" size="16" />
            </button>
            <button
              class="p-1.5 text-n-slate-10 hover:text-ruby-11 hover:bg-ruby-3 rounded transition-colors"
              :title="t('PIPELINE.DELETE_ATTACHMENT')"
              @click="handleAttachmentDelete(attachment)"
            >
              <fluent-icon icon="delete" size="16" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!getAllAttachments || getAllAttachments.length === 0"
      class="text-center py-8"
    >
      <fluent-icon
        icon="attach"
        size="32"
        class="text-n-slate-9 mx-auto mb-2"
      />
      <p class="text-sm text-n-slate-10">
        {{ t('PIPELINE.ATTACHMENTS_EMPTY') }}
      </p>
    </div>

    <!-- Upload Modal -->
    <Modal v-model:show="showUploadModal" :on-close="closeUploadModal">
      <div class="p-6 max-w-md mx-auto">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.UPLOAD_FILE') || 'Upload de Arquivo' }}
          </h3>
          <button
            class="p-1 rounded hover:bg-n-alpha-1"
            @click="closeUploadModal"
          >
            <fluent-icon icon="dismiss" size="20" class="text-n-slate-10" />
          </button>
        </div>

        <!-- Drag & Drop Zone -->
        <div
          class="border-2 border-dashed rounded-lg p-8 text-center transition-colors"
          :class="isDragging ? 'border-n-brand bg-n-alpha-1' : 'border-n-weak'"
          @dragenter="handleDragEnter"
          @dragleave="handleDragLeave"
          @dragover="handleDragOver"
          @drop="handleDrop"
        >
          <fluent-icon
            icon="arrow-upload"
            size="48"
            class="mx-auto mb-4"
            :class="isDragging ? 'text-n-brand' : 'text-n-slate-10'"
          />
          <p class="text-sm text-n-slate-11 mb-2">
            {{
              t('PIPELINE.DRAG_DROP_FILE') || 'Arraste e solte o arquivo aqui'
            }}
          </p>
          <p class="text-xs text-n-slate-10 mb-4">
            {{ t('PIPELINE.OR') || 'ou' }}
          </p>
          <label class="cursor-pointer">
            <input
              type="file"
              class="hidden"
              accept="*/*"
              @change="handleFileSelect"
            />
            <span
              class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-n-brand hover:brightness-110 rounded-lg transition-colors"
            >
              <fluent-icon icon="folder" size="16" />
              {{ t('PIPELINE.SELECT_FILE') || 'Selecionar arquivo' }}
            </span>
          </label>
        </div>

        <!-- Selected File Info -->
        <div v-if="selectedFile" class="mt-4 p-3 bg-n-solid-3 rounded-lg">
          <div class="flex items-center gap-3">
            <fluent-icon icon="document" size="20" class="text-n-slate-10" />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ selectedFile.name }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ formatFileSize(selectedFile.size) }}
              </p>
            </div>
            <button
              class="p-1 text-n-slate-10 hover:text-ruby-11"
              @click="selectedFile = null"
            >
              <fluent-icon icon="dismiss" size="16" />
            </button>
          </div>
        </div>

        <!-- Progress Bar -->
        <div v-if="isUploading" class="mt-4">
          <div class="flex items-center justify-between mb-1">
            <span class="text-xs text-n-slate-10">{{
              t('PIPELINE.UPLOADING') || 'Enviando...'
            }}</span>
            <span class="text-xs text-n-slate-10">{{ uploadProgress }}%</span>
          </div>
          <div class="w-full bg-n-solid-3 rounded-full h-2">
            <div
              class="bg-n-brand h-2 rounded-full transition-all duration-300"
              :style="{ width: uploadProgress + '%' }"
            />
          </div>
        </div>

        <!-- Success Message -->
        <div
          v-if="uploadSuccess"
          class="mt-4 p-3 bg-teal-3 rounded-lg flex items-center gap-2"
        >
          <fluent-icon icon="checkmark-circle" size="20" class="text-teal-11" />
          <span class="text-sm text-teal-11">
            {{ t('PIPELINE.UPLOAD_SUCCESS') || 'Arquivo enviado com sucesso!' }}
          </span>
        </div>

        <!-- Error Message -->
        <div
          v-if="uploadError"
          class="mt-4 p-3 bg-ruby-3 rounded-lg flex items-center gap-2"
        >
          <fluent-icon icon="error-circle" size="20" class="text-ruby-11" />
          <span class="text-sm text-ruby-11">
            {{ uploadError }}
          </span>
        </div>

        <!-- Actions -->
        <div class="mt-6 flex justify-end gap-3">
          <button
            class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
            @click="closeUploadModal"
          >
            {{ t('PIPELINE.CANCEL') || 'Cancelar' }}
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white bg-n-brand hover:brightness-110 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!selectedFile || isUploading"
            @click="uploadSelectedFile"
          >
            {{
              isUploading
                ? t('PIPELINE.UPLOADING') || 'Enviando...'
                : t('PIPELINE.UPLOAD') || 'Enviar'
            }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>
